import { erpDefinitionsLoaded, erpDefinitionTexts } from './store';
import type { ErpFlatTexts, ErpLanguage } from './types';

/**
 * Where the served texts go. The ERP cannot reach its own vue-i18n instance from
 * outside a component — Connect owns i18n — so the shell registers a sink
 * (`connect/src/integration/erpDefinitions.ts`) that merges them into the ERP's
 * message table. `messages` is nested like a locale file.
 */
export type ErpDefinitionTextSink = (lang: ErpLanguage, messages: Record<string, unknown>) => void;

/**
 * What of each language goes to the sink. German is merged whole. The English
 * table gets every served key too — German, the way the shell's own tables lay
 * English over German (connect `inLocale`), so `te()` finds a moved key in an
 * English session as it did before — and English only where the ERP always had
 * it: the menu, the cockpit and the tasks navigation (its shell surface follows
 * the shell language, I-2348, see frontend/erp/CLAUDE.md), plus the five
 * clarification columns of the attendance review month table. The ERP's
 * screens stay German.
 */
const ENGLISH_PREFIXES = ['menu.', 'masterdata.cockpit.', 'nav.'];
const ENGLISH_KEYS = new Set([
  'timemanagement.attendance_review.month_table.columns.review_reason_codes',
  'timemanagement.attendance_review.month_table.columns.is_clarified',
  'timemanagement.attendance_review.month_table.columns.clarification_reason',
  'timemanagement.attendance_review.month_table.columns.clarified_at',
  'timemanagement.attendance_review.month_table.columns.clarified_by',
]);

function englishAllowed(key: string): boolean {
  return ENGLISH_KEYS.has(key) || ENGLISH_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * `{"a.b.c": "x"}` → `{a: {b: {c: "x"}}}`. A key that is both a text and the
 * start of another key (`a.b` and `a.b.c`) cannot be nested: the first one stays,
 * the other is skipped with a warning.
 */
export function unflattenTexts(flat: ErpFlatTexts): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const [key, text] of Object.entries(flat)) {
    const path = key.split('.');
    let node = root;
    let clash = false;
    for (const part of path.slice(0, -1)) {
      const next = Object.hasOwn(node, part) ? node[part] : (node[part] = {});
      if (typeof next !== 'object' || next === null) {
        clash = true;
        break;
      }
      node = next as Record<string, unknown>;
    }
    const last = path[path.length - 1];
    if (clash || (Object.hasOwn(node, last) && typeof node[last] === 'object')) {
      console.warn(`[erp] definition text "${key}" clashes with another key and was skipped`);
      continue;
    }
    node[last] = text;
  }
  return root;
}

let sink: ErpDefinitionTextSink | null = null;

/**
 * What the sink gets of one language: the loaded texts that pass the policy,
 * nested like a locale file. Tests merge it over the locale files to see the
 * table the shell builds at run time.
 */
export function servedErpMessages(lang: ErpLanguage): Record<string, unknown> {
  const german = erpDefinitionTexts('de');
  if (lang === 'de') return unflattenTexts(german);
  const table: ErpFlatTexts = { ...german };
  for (const [key, text] of Object.entries(erpDefinitionTexts('en'))) {
    if (englishAllowed(key)) table[key] = text;
  }
  return unflattenTexts(table);
}

function deliver(target: ErpDefinitionTextSink): void {
  for (const lang of ['de', 'en'] as const) target(lang, servedErpMessages(lang));
}

/**
 * Registers the shell's sink; if the definitions are already in, it gets their
 * texts at once. Call it once at shell start-up.
 */
export function setErpDefinitionTextSink(next: ErpDefinitionTextSink | null): void {
  sink = next;
  if (sink && erpDefinitionsLoaded.value) deliver(sink);
}

/** Hands the loaded texts to the registered sink, if there is one. */
export function deliverErpDefinitionTexts(): void {
  if (sink) deliver(sink);
}
